import { useQuery } from "@tanstack/react-query";
import { type PaySchedule, type PayFrequency } from "@shared/utils/payrollSchedule";

/** Pay group as returned by GET /api/clients/:clientId/pay-groups. */
export interface PayGroup {
  id: number;
  clientId: number;
  name: string;
  isDefault: boolean;
  payFrequency: string;
  payDay: string;
  periodStartDate: string | null;
}

/**
 * Fetches a client's pay groups and exposes a resolver that turns an employee's
 * payGroupId into a PaySchedule (frequency + anchor) for FSA proration. Falls
 * back to the client's default group when the employee has no assignment.
 * Shared by the enrollment surfaces so they prorate identically.
 */
export function usePaySchedule(clientId: number | null | undefined) {
  const { data: payGroups = [], isLoading } = useQuery<PayGroup[]>({
    queryKey: [clientId ? `/api/clients/${clientId}/pay-groups` : ""],
    enabled: !!clientId,
    // Pay-group config (frequency/anchor) can change between sessions; the global
    // default is staleTime: Infinity, so refetch on mount to keep proration current.
    staleTime: 0,
  });

  const resolvePaySchedule = (payGroupId: number | null | undefined): PaySchedule | null => {
    if (!payGroups.length) return null;
    const group =
      (payGroupId != null && payGroups.find((g) => g.id === payGroupId)) ||
      payGroups.find((g) => g.isDefault) ||
      payGroups[0];
    if (!group) return null;
    return {
      payFrequency: (group.payFrequency || "biweekly") as PayFrequency,
      anchorDate: group.periodStartDate ? new Date(group.periodStartDate) : null,
    };
  };

  return { payGroups, resolvePaySchedule, isLoading: isLoading && !!clientId };
}
